import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flame, Clock, User, X, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

/**
 * QuickBookSection
 * ================
 * Renders a "🔥 Quick Book Available" trigger button. Clicking it opens a
 * full-screen modal with today's available quick-book slots.
 *
 * Two slot flavors:
 *   1. STANDARD Chapo/Duse slots — $120 flat for 3 hours, always available
 *      when engineer is free.
 *   2. TRAP SPADE LAST-MINUTE — $40 flash deal, only surfaces if the slot
 *      starts within 5 hours from now, never Fri/Sat/Sun.
 *
 * Contact modal collects Name + Phone (+ optional Email), then posts to
 * `POST /api/quick-book/checkout` which returns a Stripe checkout URL we
 * redirect to.
 */
const QuickBookSection = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchSlots = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/quick-book/slots`);
      setSlots(r.data.slots || []);
    } catch (e) {
      console.error('Quick book fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Prefill from logged-in user
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u.name) setName(u.name);
        if (u.phone) setPhone(u.phone);
        if (u.email) setEmail(u.email);
      }
    } catch (e) {
      // no-op
    }
    fetchSlots();
    // Refresh every 60s so trap-spade last-minute slots surface / disappear
    // in near real-time as the 5-hour window slides.
    const iv = setInterval(fetchSlots, 60000);
    return () => clearInterval(iv);
  }, [fetchSlots]);

  const trapSlots = [];  // Trap Spade is no longer in quick book; $40 last-minute fee lives in the full booking flow
  const budgetSlots = slots.filter((s) => s.kind === 'budget');
  const standardSlots = slots.filter((s) => s.kind !== 'trap_spade_last_minute' && s.kind !== 'budget');
  const hasAnySlots = slots.length > 0;

  const openBookingModal = (slot, engineer) => {
    setSelection({ slot, engineer });
    setContactOpen(true);
    setPickerOpen(false);
  };

  const closeAll = () => {
    if (submitting) return;
    setPickerOpen(false);
    setContactOpen(false);
    setSelection(null);
  };

  const submit = async () => {
    if (!selection) return;
    if (!name.trim() || !phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Phone number must have at least 10 digits');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/quick-book/checkout`, {
        engineer_id: selection.engineer.id,
        date: selection.slot.date,
        time: selection.slot.start_time,
        slot_key: selection.slot.slot_key,
        name: name.trim(),
        phone: digits,
        email: email.trim().toLowerCase() || undefined,
        payment_type: 'full',
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Payment setup failed. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong.';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (loading || !hasAnySlots) return null;

  return (
    <section
      data-testid="quick-book-section"
      className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-4"
    >
      {/* Trigger button */}
      <button
        data-testid="quick-book-open-btn"
        onClick={() => setPickerOpen(true)}
        className="w-full group relative overflow-hidden rounded-2xl border border-orange-400/50 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-4 sm:p-5 shadow-lg hover:shadow-orange-500/40 transition-all hover:scale-[1.01] text-left"
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="p-2.5 sm:p-3 rounded-xl bg-white/15 border border-white/30 flex-shrink-0">
            <Flame className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="currentColor" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg sm:text-xl font-bold text-white tracking-tight">
                Quick Book Available
              </span>
              {trapSlots.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-300 text-purple-900 text-[10px] font-black uppercase tracking-wider">
                  <Zap className="w-3 h-3" fill="currentColor" />
                  Trap Spade $40 Flash
                </span>
              )}
            </div>
            <div className="text-xs sm:text-sm text-white/90 mt-0.5">
              {standardSlots.length > 0 && (
                <span>{standardSlots.length} open · $100 · Chapo/Duse</span>
              )}
              {standardSlots.length > 0 && budgetSlots.length > 0 && <span> · </span>}
              {budgetSlots.length > 0 && (
                <span>{budgetSlots.length} at $100 · 3 hrs</span>
              )}
              {trapSlots.length > 0 && (standardSlots.length > 0 || budgetSlots.length > 0) && <span> · </span>}
              {trapSlots.length > 0 && (
                <span className="font-semibold">
                  {trapSlots.length} Trap Spade $40 flash
                </span>
              )}
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center px-4 py-2 rounded-full bg-white text-orange-600 font-bold text-sm group-hover:bg-orange-50 transition-colors flex-shrink-0">
            View slots →
          </span>
          <span className="sm:hidden text-white/90 font-bold flex-shrink-0">→</span>
        </div>
      </button>

      {/* Picker modal — shows all available slots */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/85 backdrop-blur px-3 py-4 sm:py-8 overflow-y-auto"
          onClick={closeAll}
        >
          <div
            className="w-full max-w-2xl bg-gradient-to-b from-purple-950 to-black border border-purple-500/40 rounded-2xl p-4 sm:p-6 shadow-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-400" fill="currentColor" />
                  <h3 className="text-xl font-bold text-white">Quick Book</h3>
                </div>
                <p className="text-xs text-white/60 mt-0.5">
                  Pick a slot and pay in seconds. Session confirmed on payment.
                </p>
              </div>
              <button
                data-testid="quick-book-close-picker"
                onClick={closeAll}
                className="text-white/60 hover:text-white p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Trap Spade flash section */}
            {trapSlots.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Zap className="w-4 h-4 text-yellow-400" fill="currentColor" />
                  <span className="text-xs uppercase tracking-widest text-yellow-400 font-bold">
                    Trap Spade · $40 Flash Deal · Starts within 5 hours
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {trapSlots.map((slot) => (
                    <button
                      key={`ts-${slot.date}-${slot.slot_key}`}
                      data-testid={`quick-book-ts-${slot.slot_key}`}
                      onClick={() => openBookingModal(slot, { id: 1, name: 'Trap Spade' })}
                      className="text-left rounded-xl border-2 border-yellow-400/60 bg-gradient-to-br from-yellow-500/15 to-orange-500/15 hover:from-yellow-500/25 hover:to-orange-500/25 p-3 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-yellow-300 font-bold">
                            {slot.date_display}
                          </div>
                          <div className="text-base font-bold text-white mt-0.5">
                            {slot.start_time}–{slot.end_time}
                          </div>
                          <div className="text-[11px] text-yellow-200/80 mt-0.5">
                            with Trap Spade · 3 hrs
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black text-yellow-300 leading-none">$40</div>
                          <div className="text-[9px] text-yellow-200/70 mt-0.5">flat</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Standard $120 slots */}
            {standardSlots.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-purple-300 font-bold mb-2">
                  $100 · 3 Hours · Chapo or Duse
                  <span className="text-orange-300 font-normal normal-case ml-2">
                    (+$20 rush fee if starting within 4 hrs)
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {standardSlots.map((slot) => (
                    <div
                      key={`std-${slot.date}-${slot.slot_key}`}
                      data-testid={`quick-book-slot-${slot.slot_key}`}
                      className={`rounded-xl border p-3 flex flex-col ${
                        slot.is_last_minute
                          ? 'border-orange-400/60 bg-gradient-to-br from-orange-500/10 to-red-500/10'
                          : 'border-purple-500/30 bg-black/40'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider text-orange-300 font-bold">
                          {slot.date_display}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-white/50 flex items-baseline gap-1">
                          {slot.is_last_minute && (
                            <span className="text-orange-400 font-bold" title="Includes $20 last-minute fee">
                              +${slot.last_minute_fee}
                            </span>
                          )}
                          <span>${slot.price}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Clock className="w-4 h-4 text-purple-300 flex-shrink-0" />
                        <span className="text-sm font-bold text-white">
                          {slot.start_time}–{slot.end_time}
                        </span>
                      </div>
                      {slot.is_last_minute && (
                        <div className="text-[9px] uppercase tracking-wider text-orange-300 font-semibold mb-1.5">
                          ⚡ Last-minute · +$20 rush fee
                        </div>
                      )}
                      <div className="flex flex-col gap-1.5 mt-auto">
                        {slot.engineers.map((eng) => (
                          <button
                            key={eng.id}
                            data-testid={`quick-book-${slot.slot_key}-eng-${eng.id}`}
                            onClick={() => openBookingModal(slot, eng)}
                            className="group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-purple-600/30 hover:bg-purple-600/60 border border-purple-400/40 hover:border-purple-300 transition-all"
                          >
                            <span className="flex items-center gap-1.5 text-xs font-semibold text-white">
                              <User className="w-3 h-3" />
                              {eng.name}
                            </span>
                            <span className="text-[9px] font-bold text-orange-300 group-hover:text-yellow-300">
                              BOOK →
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* $100 / 3 hours — engineer auto-assigned (Duse or Jelly) */}
            {budgetSlots.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-fuchsia-300 font-bold mb-2 flex items-center gap-2">
                  <span>$100 · 3 Hours · Just pick a time</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {budgetSlots.map((slot) => (
                    <div
                      key={`bgt-${slot.date}-${slot.slot_key}`}
                      data-testid={`quick-book-slot-${slot.slot_key}`}
                      className="rounded-xl border border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-900/20 to-pink-900/20 p-3 flex flex-col"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-bold">
                          {slot.date_display}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-white/70">
                          ${slot.price}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Clock className="w-4 h-4 text-fuchsia-300 flex-shrink-0" />
                        <span className="text-sm font-bold text-white">
                          {slot.start_time}–{slot.end_time}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1.5 mt-auto">
                        <button
                          data-testid={`quick-book-${slot.date}-${slot.slot_key}-book`}
                          onClick={() => openBookingModal(slot, slot.engineers[Math.floor(Math.random() * slot.engineers.length)])}
                          className="group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-fuchsia-600/30 hover:bg-fuchsia-600/60 border border-fuchsia-400/40 hover:border-fuchsia-300 transition-colors"
                        >
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-white">
                            <User className="w-3 h-3" />
                            Engineer assigned for you
                          </span>
                          <span className="text-[9px] font-bold text-fuchsia-200 group-hover:text-yellow-200">
                            BOOK →
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact modal */}
      {contactOpen && selection && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur px-4"
          onClick={closeAll}
        >
          <div
            className="w-full max-w-sm bg-gradient-to-b from-purple-950 to-black border border-purple-500/40 rounded-2xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-orange-300 font-bold">
                  {selection.slot.kind === 'trap_spade_last_minute' ? 'Flash Deal' : 'Quick Book'}
                </div>
                <h3 className="text-lg font-bold text-white mt-0.5">
                  {selection.engineer.name} · {selection.slot.start_time}
                </h3>
                <p className="text-xs text-white/60 mt-0.5">
                  {selection.slot.date_display} · ${selection.slot.price} for {selection.slot.hours} hrs
                </p>
              </div>
              <button
                data-testid="quick-book-close-contact"
                onClick={closeAll}
                className="text-white/60 hover:text-white p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-purple-300 mb-1">Your Name</label>
                <input
                  data-testid="quick-book-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 rounded-lg bg-black/50 border border-purple-500/40 text-white text-sm focus:outline-none focus:border-purple-400"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-purple-300 mb-1">Phone Number</label>
                <input
                  data-testid="quick-book-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 555-5555"
                  className="w-full px-3 py-2 rounded-lg bg-black/50 border border-purple-500/40 text-white text-sm focus:outline-none focus:border-purple-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-purple-300 mb-1">Email (optional)</label>
                <input
                  data-testid="quick-book-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com — for receipt"
                  className="w-full px-3 py-2 rounded-lg bg-black/50 border border-purple-500/40 text-white text-sm focus:outline-none focus:border-purple-400"
                />
              </div>
            </div>

            <button
              data-testid="quick-book-submit"
              onClick={submit}
              disabled={submitting}
              className="w-full mt-5 py-3 rounded-full bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-500 hover:to-orange-400 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending to checkout…
                </>
              ) : (
                <>Pay ${selection.slot.price} → Confirm Session</>
              )}
            </button>

            <p className="text-[10px] text-white/40 text-center mt-3">
              You&apos;ll be redirected to Stripe for payment. Session is confirmed on payment.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default QuickBookSection;
